import type React from "react";
import {Button, Stack, Typography} from "@mui/material";
import ArrowIcon from '~/icons/svg/ArrowIcon.svg?react'
import EditIcon from '~/icons/svg/EditIcon.svg?react'
import BuildingIcon from '~/icons/svg/BuildingIcon.svg?react'
import StoreIcon from '~/icons/svg/StoreIcon.svg?react'
import {Tag} from "~/components/tag/Tag";
import {STATUS_CHIP} from "~/components/status-chip/statusChips";
import type {
    BuQualification,
    ComplianceRequirement,
} from "~/pages/supplier-360/types/Supplier360Types";
import {LAYER_LABEL} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";

type RecordDetailHeaderProps = {
    record: ComplianceRequirement;
    supplierName: string;
    qualification: BuQualification | undefined;
    onBack: () => void;
    onEdit: () => void;
};

/** The detail page's head (`sxc-drillhead`): back link, state + layer chips, title, Edit record. */
export const RecordDetailHeader: React.FC<RecordDetailHeaderProps> = ({
                                                                          record,
                                                                          supplierName,
                                                                          qualification,
                                                                          onBack,
                                                                          onEdit,
                                                                      }) => {
    const isCompanyWide = record.layer === 'company_wide';
    const layerLabel = isCompanyWide
        ? LAYER_LABEL.company_wide
        : qualification?.business_unit?.bu_name ?? LAYER_LABEL.business_unit;
    const meta = [record.kind, isCompanyWide ? null : qualification?.category].filter(Boolean).join(' · ');
    const appliesTo = isCompanyWide
        ? 'all business units'
        : [qualification?.business_unit?.bu_name, qualification?.region].filter(Boolean).join(' · ');

    return (
        <Stack gap={1}>
            <Button
                size="small"
                onClick={onBack}
                startIcon={<ArrowIcon width={16} height={16}/>}
                sx={{alignSelf: 'flex-start', textTransform: 'none'}}
            >
                Compliance records
            </Button>

            <Stack direction="row" gap={1} sx={{alignItems: 'center', flexWrap: 'wrap'}}>
                {STATUS_CHIP[record.state]}
                <Tag
                    size="small"
                    variant="outlined"
                    color={isCompanyWide ? 'default' : 'secondary'}
                    icon={isCompanyWide ? <BuildingIcon width={14} height={14}/> : <StoreIcon width={14} height={14}/>}
                    label={layerLabel}
                />
                {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
            </Stack>

            <Stack direction="row" gap={2} alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{record.name}</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon width={14} height={14}/>}
                    onClick={onEdit}
                    sx={{textTransform: 'none'}}
                >
                    Edit record
                </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary">
                {`Compliance record · ${supplierName} · applies to ${appliesTo}`}
            </Typography>
        </Stack>
    );
};
